import { FORBIDDEN_EVENT } from './access'

// Base URL for the primary API endpoint
const BASE_URL = `${import.meta.env.VITE_API_ENDPOINT}`

// A 403 that is a permission refusal, not an expired token: tell the
// PermissionsProvider, which re-reads the role (it may have changed in Settings).
// API Gateway answers a route it does not have with 403 "Missing Authentication
// Token": that is not about the role, so it is not announced.
const notifyForbidden = (method, endpoint, body = '') => {
  if (String(body).includes('Missing Authentication Token')) return
  try {
    window.dispatchEvent(new CustomEvent(FORBIDDEN_EVENT, { detail: { method, endpoint } }))
  } catch (_) {
    // No window (tests outside jsdom): nothing to tell.
  }
}
const IMAGE_SERVICE_URL = (import.meta.env.VITE_IMAGE_SERVICE_URL || 'http://localhost:5003').replace(/\/+$/, '')

// Function to upload the file to a signed URL
export const uploadFileToSignedUrlRequest = async (signedUrl, method = 'PUT', file) => {
  try {
    const headers = new Headers()
    // Support File/Blob uploads (useInference passes Blob/File, which uses `.type`)
    const contentType = file?.fileType || file?.type || 'application/octet-stream'
    headers.append('Content-Type', contentType)

    const options = {
      method,
      headers,
      body: file, // Pass the actual file data as the request body
    }

    const resp = await fetch(signedUrl, options) // PUT request to upload the file
    if (!resp.ok) {
      const txt = await resp.text().catch(() => '')
      throw new Error(`Signed URL upload failed (${resp.status}) ${txt}`.trim())
    }
    console.log('File uploaded successfully.')
  } catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}

// Store for the refresh token callback (set by components that need 403 handling)
let refreshTokenCallback = null
let logoutCallback = null

// Coalesces concurrent refresh requests on the SC side. Without this, every
// expired-token request that hits makeAuthorizedRequest in parallel posts its
// own SC_REQUEST_REFRESH to the parent and waits for an EDC_AUTH_REFRESH —
// the parent dedupes /auth/refresh via inflightRefresh, but we still get N
// round-trips and N onMessage listeners contending for the response. One
// promise per refresh window keeps the choreography sane and means callers
// awaiting "the current refresh" all see the same fresh token.
let inflightRefreshPromise = null
const refreshTokenDeduped = () => {
  if (!refreshTokenCallback) return Promise.reject(new Error('No refresh callback configured'))
  if (!inflightRefreshPromise) {
    inflightRefreshPromise = Promise.resolve()
      .then(() => refreshTokenCallback())
      .finally(() => {
        inflightRefreshPromise = null
      })
  }
  return inflightRefreshPromise
}

/**
 * Decode a JWT and report whether its `exp` claim is in the past.
 *
 * 403s from the API Gateway authorizer have two shapes — token expired AND
 * permission denied — and they're indistinguishable by status code or body
 * (both come back with the canned IAM "explicit deny" message). Without this
 * check the retry path keeps hammering with the same valid token, fails
 * again, and triggers a spurious logout. Only refresh when the JWT is
 * actually expired; otherwise treat the 403 as a real permission denial and
 * surface the error to the caller without nuking the session.
 */
function isJwtExpired(token) {
  if (!token || typeof token !== 'string') return false
  const parts = token.split('.')
  if (parts.length !== 3) return false
  try {
    // base64url → base64 → JSON. atob doesn't accept URL-safe input.
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    )
    if (typeof payload.exp !== 'number') return false
    // Treat anything within 5s of expiry as expired so we don't race the
    // gateway's clock skew tolerance.
    return payload.exp * 1000 < Date.now() + 5000
  } catch (_) {
    return false
  }
}

/**
 * Set callbacks for handling 403 errors globally
 * This should be called from your auth context or app initialization
 * @param {Function} refreshFn - Function to refresh the token
 * @param {Function} logoutFn - Function to logout the user
 */
export const setAuthCallbacks = (refreshFn, logoutFn) => {
  refreshTokenCallback = refreshFn
  logoutCallback = logoutFn
}

// Maximum number of refresh-and-retry attempts on 403 with an expired token
// before we give up and log the user out. Three covers the realistic worst
// case: original request, a retry against a freshly-refreshed token that
// races with another in-flight refresh, and one more cushion.
const MAX_AUTH_RETRIES = 3

// Function to create authorized requests with automatic 403 handling.
// retryCount counts how many refresh-and-retry attempts have already
// happened. Tracked numerically (rather than a boolean isRetry) so we can
// allow up to MAX_AUTH_RETRIES on transient or racy refresh paths.
export const makeAuthorizedRequest = async (
  endpoint,
  method = 'GET',
  data = null,
  token,
  retryCount = 0,
) => {
  if (!token) {
    console.error('Authorization token is missing')
    throw new Error('Authorization token is missing')
  }

  // Stale-closure guard. Callers grab idToken via useAuth() and may close
  // over it in callbacks/effects; if a refresh fired between the closure
  // capture and the actual fetch, the captured token is now stale even
  // though auth-context already mirrored the fresh one into localStorage.
  // Always prefer the localStorage token when it's different and not
  // expired — same source of truth the AuthProvider writes to.
  try {
    const stored = typeof localStorage !== 'undefined'
      ? localStorage.getItem('edc_access_token')
      : null
    if (stored && stored !== token && !isJwtExpired(stored)) {
      // Only log when the swap actually changes which token gets sent —
      // helps trace "I refreshed but the old token went out anyway" issues.
      console.log(
        `[api] Swapping stale closure token for fresher localStorage token on ${method} ${endpoint}`,
      )
      token = stored
    }
  } catch (_) {
    // localStorage access can throw in privacy modes; fall through with
    // the passed-in token.
  }

  // Pre-flight expiry check. After the closure-vs-localStorage swap above,
  // if the token we'd send is STILL expired (both copies are old, or
  // localStorage hasn't been written yet because a refresh is in flight),
  // refresh proactively rather than firing the request, eating a 403, and
  // refreshing on the bounce. Two concrete reasons:
  //   1. /design (and the other persist calls) typically fire after a
  //      long-running image-generation flow. By then a refresh triggered
  //      mid-flow may have completed for other calls but the closure
  //      idToken in the persist callback is still stale; localStorage may
  //      also still be stale if THIS call beat the refresh into the queue.
  //      The expired token reaches the server and 403s before the retry
  //      path fixes it — visible to the user as a failed /design with old
  //      token while every later call works.
  //   2. The 403 round-trip costs ~150–500ms. Refreshing pre-flight is a
  //      single postMessage hop — usually <50ms — and avoids the noisy
  //      403 in the network tab.
  // Only on retryCount === 0 to keep the recursive retry path's own
  // expiry check (lines below) authoritative on subsequent attempts.
  if (retryCount === 0 && refreshTokenCallback && isJwtExpired(token)) {
    try {
      console.log(
        `[api] Pre-flight: token expired for ${method} ${endpoint}, refreshing before send`,
      )
      const fresh = await refreshTokenDeduped()
      if (fresh && !isJwtExpired(fresh)) {
        token = fresh
      }
    } catch (e) {
      // Pre-flight refresh failed — fall through and let the 403 retry
      // path try again. Don't bail the request here: the user may have
      // reconnected, the parent may now be reachable, etc.
      console.warn(
        `[api] Pre-flight refresh failed for ${method} ${endpoint}, sending with current token:`,
        e,
      )
    }
  }

  const headers = new Headers()
  headers.append('token', `Bearer ${token}`)

  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData
  if (!isFormData) {
    headers.append('Content-Type', 'application/json')
  }

  const options = {
    method,
    headers,
    body: isFormData ? data : data ? JSON.stringify(data) : null,
  }

  const baseUrl = endpoint.startsWith('/generate_hunyuan_3d') ? IMAGE_SERVICE_URL : BASE_URL

  try {
    const response = await fetch(`${baseUrl}${endpoint}`, options)

    // Handle 403 Forbidden. Two distinct cases share this status from the
    // API Gateway authorizer:
    //   - Token expired → refresh + retry (up to MAX_AUTH_RETRIES).
    //   - Permission denied → bubble up; never refresh.
    //
    // Note: 403 is intentionally NEVER allowed to redirect/log the user
    // out from this layer. Earlier behavior auto-redirected to the
    // staffing-app login page on any sustained 403, which masked real
    // backend permission bugs and yanked users mid-task. The error is
    // surfaced as a normal Error and individual callers decide what to
    // do (toast, retry button, etc.).
    if (response.status === 403 && retryCount < MAX_AUTH_RETRIES && refreshTokenCallback) {
      if (!isJwtExpired(token)) {
        console.warn(
          `⚠️ 403 on ${method} ${endpoint} but JWT is not expired — treating as permission denial.`,
        )
        const errorText = await response.text()
        notifyForbidden(method, endpoint, errorText)
        throw new Error(`Request failed with status: 403 - ${errorText}`)
      }

      console.warn(
        `⚠️ Token expired on ${method} ${endpoint}. Refreshing and retrying (attempt ${retryCount + 1}/${MAX_AUTH_RETRIES})…`,
      )

      try {
        const newToken = await refreshTokenDeduped()
        // Defensive: if the callback handed back the same expired token,
        // retrying will just 403 again. Skip retry, throw the original
        // error to the caller.
        if (!newToken || newToken === token || isJwtExpired(newToken)) {
          throw new Error('Refresh did not produce a fresh token')
        }
        console.log(`✅ Token refreshed. Retrying request (attempt ${retryCount + 1}/${MAX_AUTH_RETRIES})…`)
        return await makeAuthorizedRequest(endpoint, method, data, newToken, retryCount + 1)
      } catch (refreshError) {
        console.error(
          `❌ Refresh attempt ${retryCount + 1}/${MAX_AUTH_RETRIES} failed (NOT redirecting):`,
          refreshError,
        )
        // Do NOT call logoutCallback. Throw a 403-style error so callers
        // can render an inline message; the user keeps their session and
        // can retry, sign back in manually, or contact support.
        throw new Error(`Request failed with status: 403 - token refresh unavailable`)
      }
    }

    // Hit the retry cap — the JWT keeps coming back expired, the refresher
    // can't produce a usable token. Bail out cleanly without redirecting.
    if (response.status === 403 && retryCount >= MAX_AUTH_RETRIES) {
      console.error(
        `❌ Hit ${MAX_AUTH_RETRIES} refresh-and-retry attempts on ${method} ${endpoint}, giving up (NOT redirecting).`,
      )
      throw new Error(`Request failed with status: 403 - exceeded ${MAX_AUTH_RETRIES} retries`)
    }

    // Handle other non-OK responses
    if (!response.ok) {
      const errorText = await response.text()
      if (response.status === 403 && !isJwtExpired(token)) notifyForbidden(method, endpoint, errorText)
      throw new Error(`Request failed with status: ${response.status} - ${errorText}`)
    }

    return await response.json() // Parse JSON response
  } catch (error) {
    console.error('Error in authorized API request:', error)
    throw error // Rethrow the error to handle it in the caller function
  }
}

// Function to get a signed URL from the API
export const getSignedUrl = async (fileData, token) => {
  return await makeAuthorizedRequest('/upload/getsignedurl', 'POST', fileData, token)
}

// Exported function to upload the file to the signed URL
export const uploadFileToSignedUrl = async (signedUrl, file) => {
  return await uploadFileToSignedUrlRequest(signedUrl, 'PUT', file) // Correctly pass the file
}

// Function to post inference requests to the queue
export const postToInferenceQueue = async (body, token) => {
  return await makeAuthorizedRequest('/inference/queue', 'POST', body, token)
}

export const uploadToSignedUrl = async (signedUrl, file) => {
  try {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    })

    if (!response.ok) {
      throw new Error(`Upload failed with status: ${response.status}`)
    }
    return response
  } catch (error) {
    console.error('Error uploading to signed URL:', error)
    throw error
  }
}

/**
 * Fetch every page of a list endpoint and return the rows as one array.
 *
 * The list endpoints now cap any single response (see each Lambda's
 * pagination.js), so a caller that genuinely needs the whole set — the editor
 * building its asset panel, or a project lookup map — cannot just fire one
 * unpaginated request any more and trust it got everything.
 *
 * Passing page/limit opts the endpoint into its envelope shape
 * ({ [collection], pagination }); a backend that still answers with a bare
 * array is treated as a single complete page.
 *
 * @param {string} endpoint   path, with or without an existing query string
 * @param {string} token      bearer token
 * @param {object} options    { collection, pageSize, maxPages }
 */
export const fetchAllPages = async (
  endpoint,
  token,
  { collection, pageSize = 500, maxPages = 20 } = {},
) => {
  const rows = []
  let page = 1
  let totalPages = 1

  do {
    const separator = endpoint.includes('?') ? '&' : '?'
    const url = `${endpoint}${separator}page=${page}&limit=${pageSize}`
    const response = await makeAuthorizedRequest(url, 'GET', null, token)

    if (Array.isArray(response)) {
      // Un-paginated backend: what came back is all there is.
      return response
    }

    const batch = response && Array.isArray(response[collection]) ? response[collection] : []
    rows.push(...batch)

    totalPages = Math.max(1, response?.pagination?.pages || 1)
    page += 1

    // Nothing came back — stop rather than spin on a malformed response.
    if (batch.length === 0) break
  } while (page <= totalPages && page <= maxPages)

  return rows
}

export default makeAuthorizedRequest
